/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {clientGoogleGeolocate} from '@amblique/king-living/app/utils/google-client-geolocation';
import {buildSessionKey, clearSession, readSession, writeSession} from '@amblique/king-living/app/utils/session-utils';

/** Session cache TTL for browser geo (geolocate + reverse geocode). */
const CACHE_TTL_MS = 30 * 60 * 1000;
const SESSION_KEY = 'browser-geo';

let inflightEntry = null;

/**
 * @param {string|undefined|null} apiKey
 * @returns {string}
 */
function normalizeApiKey(apiKey) {
  return typeof apiKey === 'string' ? apiKey.trim() : '';
}

/**
 * @param {string|undefined|null} siteId
 * @returns {string}
 */
function normalizeSiteId(siteId) {
  return typeof siteId === 'string' && siteId.trim() ? siteId.trim() : 'default';
}

/**
 * @param {string} siteId
 * @returns {string}
 */
function buildGeoSessionKey(siteId) {
  return buildSessionKey(normalizeSiteId(siteId), SESSION_KEY);
}

/**
 * @param {{ apiKey: string, timestamp: number }|null} entry
 * @param {string} apiKey
 * @returns {boolean}
 */
function isCacheEntryFresh(entry, apiKey) {
  return Boolean(entry && entry.apiKey === apiKey && Date.now() - entry.timestamp < CACHE_TTL_MS);
}

/**
 * @param {string} siteId
 * @param {string} apiKey
 * @returns {string}
 */
function buildInflightKey(siteId, apiKey) {
  return `${normalizeSiteId(siteId)}\n${apiKey}`;
}

/**
 * @param {Object} result
 * @returns {Object}
 */
function toSlimResult(result) {
  return {
    location: result.location,
    ...(typeof result.accuracy === 'number' ? {accuracy: result.accuracy} : {}),
    countryCode: result.countryCode ?? null,
    regionCode: result.regionCode ?? null,
    postcode: result.postcode ?? null,
    stateCode: result.stateCode ?? null,
    addressCountryCode: result.addressCountryCode ?? null,
    city: result.city ?? null,
  };
}

/**
 * @param {string} siteId
 * @param {string} apiKey
 * @returns {Object|null}
 */
function readSessionCache(siteId, apiKey) {
  const sessionKey = buildGeoSessionKey(siteId);
  const entry = readSession(sessionKey);

  if (!isCacheEntryFresh(entry, apiKey)) {
    if (entry) {
      clearSession(sessionKey);
    }

    return null;
  }

  return entry.result ?? null;
}

/**
 * @param {string} siteId
 * @param {string} apiKey
 * @param {Object} result
 */
function writeSessionCache(siteId, apiKey, result) {
  writeSession(buildGeoSessionKey(siteId), {
    apiKey,
    timestamp: Date.now(),
    result: toSlimResult(result),
  });
}

/**
 * Clear persisted browser geo for a site (e.g. manual invalidation).
 *
 * @param {string|undefined|null} siteId
 */
export function clearBrowserGeoSession(siteId) {
  clearSession(buildGeoSessionKey(siteId));
}

/**
 * @param {Object} geocodeResults - Google Geocoding API response
 * @returns {{ countryCode: string|null, regionCode: string|null }}
 */
export function extractCountryAndAdminRegion(geocodeResults) {
  if (!geocodeResults?.results?.length) {
    return {countryCode: null, regionCode: null};
  }

  const components = geocodeResults.results[0].address_components || [];
  const country = components.find((c) => c.types?.includes('country'));
  const admin = components.find((c) => c.types?.includes('administrative_area_level_1'));

  return {
    countryCode: country?.short_name ? country.short_name.toUpperCase() : null,
    regionCode: admin?.short_name ? admin.short_name.toUpperCase() : null,
  };
}

/**
 * @param {Object|null|undefined} geocodeResults - Google Geocoding JSON
 * @returns {{postcode: string, countryCode: string, stateCode: string}|null}
 */
export function extractPostcodeRegionFromGeocode(geocodeResults) {
  const components = geocodeResults?.results?.[0]?.address_components;

  if (!components?.length) {
    return null;
  }

  const pick = (type) => components.find((c) => c.types.includes(type));
  const postal = pick('postal_code')?.long_name;
  const country = pick('country')?.short_name;
  const admin = pick('administrative_area_level_1');

  if (!postal || !country) {
    return null;
  }

  const stateCode = admin?.short_name || admin?.long_name || '';

  return {
    postcode: postal,
    countryCode: country,
    stateCode,
  };
}

/**
 * City or suburb label from reverse geocode (aligned with AddressAutocomplete parsing).
 *
 * @param {Object|null|undefined} geocodeResults - Google Geocoding JSON
 * @returns {string|null}
 */
export function extractCityFromGeocode(geocodeResults) {
  const components = geocodeResults?.results?.[0]?.address_components;

  if (!components?.length) {
    return null;
  }

  const pick = (type) => components.find((c) => c.types?.includes(type));
  const candidate =
    pick('locality') ??
    pick('sublocality_level_1') ??
    pick('sublocality') ??
    pick('postal_town') ??
    pick('administrative_area_level_2');

  const name = candidate?.long_name || candidate?.short_name;

  return name ? String(name).trim() : null;
}

/**
 * Map browser geolocation + reverse geocode result to a shipping estimate address.
 * Shape matches basket shipping address fields used by {@link formatAddress} in use-shipping-methods.
 *
 * @param {Object|null|undefined} geo
 * @param {string} [siteCountryCode='AU']
 * @returns {{postcode: string, stateCode: string, city: string|null, countryCode: string}|null}
 */
export function geoResultToShippingAddress(geo, siteCountryCode = 'AU') {
  if (!geo || typeof geo !== 'object') return null;

  const pc = geo.postcode != null ? String(geo.postcode).trim() : '';
  const st = geo.stateCode != null ? String(geo.stateCode).trim().toUpperCase() : '';
  const countryCode = (
    geo.addressCountryCode ||
    geo.countryCode ||
    siteCountryCode ||
    'AU'
  )
    .trim()
    .toUpperCase();
  const effectiveState = st || (countryCode === 'SG' ? 'SG' : '');

  if (!pc || !effectiveState) return null;

  const city = geo.city != null ? String(geo.city).trim() || null : null;

  return {postcode: pc, stateCode: effectiveState, city, countryCode};
}

/**
 * Client Google Geolocation (considerIp) + server reverse geocode.
 * Deduplicates in-flight requests and persists slim results in sessionStorage so geo redirect,
 * Canada locale, and warehouse hooks share one geolocate + one reverse geocode per tab session.
 *
 * @param {Object} args
 * @param {string} [args.siteId] - Site id for session key scoping
 * @param {string} args.apiKey - GOOGLE_GEOLOCATION_BROWSER_API_KEY site preference
 * @param {Function} args.reverseGeocode - GET /server/geocode (SLAS Bearer)
 * @returns {Promise<{
 *   location: { lat: number, lng: number },
 *   accuracy?: number,
 *   countryCode: string|null,
 *   regionCode: string|null,
 *   postcode: string|null,
 *   stateCode: string|null,
 *   addressCountryCode: string|null,
 *   city: string|null
 * }>}
 */
export async function resolveBrowserGeoFull({siteId, apiKey, reverseGeocode}) {
  const key = normalizeApiKey(apiKey);
  const normalizedSiteId = normalizeSiteId(siteId);

  if (!key) {
    throw new Error('Missing Google Geolocation API key');
  }

  const cached = readSessionCache(normalizedSiteId, key);

  if (cached) {
    return cached;
  }

  const inflightKey = buildInflightKey(normalizedSiteId, key);

  if (inflightEntry?.key === inflightKey) {
    return inflightEntry.promise;
  }

  const promise = (async () => {
    const geolocateResult = await clientGoogleGeolocate(key, {considerIp: true});
    const {lat, lng} = geolocateResult.location;
    const geocodeResults = await reverseGeocode(lat, lng);
    const {countryCode, regionCode} = extractCountryAndAdminRegion(geocodeResults);
    const postcodeRegion = extractPostcodeRegionFromGeocode(geocodeResults);
    const city = extractCityFromGeocode(geocodeResults);

    const result = toSlimResult({
      location: {lat, lng},
      ...(typeof geolocateResult.accuracy === 'number' ? {accuracy: geolocateResult.accuracy} : {}),
      countryCode,
      regionCode,
      postcode: postcodeRegion?.postcode ?? null,
      stateCode: postcodeRegion?.stateCode ?? regionCode ?? null,
      addressCountryCode: postcodeRegion?.countryCode ?? countryCode ?? null,
      city,
    });

    writeSessionCache(normalizedSiteId, key, result);

    return result;
  })();

  inflightEntry = {key: inflightKey, promise};

  try {
    return await promise;
  } finally {
    if (inflightEntry?.key === inflightKey) {
      inflightEntry = null;
    }
  }
}

/**
 * Client Google Geolocation (considerIp) + server reverse geocode → country and admin region.
 *
 * @param {Object} args
 * @param {string} [args.siteId]
 * @param {string} args.apiKey - GOOGLE_GEOLOCATION_BROWSER_API_KEY site preference
 * @param {Function} args.reverseGeocode - GET /server/geocode (SLAS Bearer)
 * @returns {Promise<{ countryCode: string|null, regionCode: string|null }>}
 */
export async function resolveBrowserGeoAddress({siteId, apiKey, reverseGeocode}) {
  const full = await resolveBrowserGeoFull({siteId, apiKey, reverseGeocode});

  return {
    countryCode: full.countryCode,
    regionCode: full.regionCode,
  };
}

/**
 * @param {Object} args
 * @param {string} [args.siteId]
 * @param {string} args.apiKey
 * @param {Function} args.reverseGeocode
 * @returns {Promise<string|null>} ISO 3166-1 alpha-2 uppercased, or null if unknown
 */
export async function resolveBrowserGeoCountryCode({siteId, apiKey, reverseGeocode}) {
  const {countryCode} = await resolveBrowserGeoFull({siteId, apiKey, reverseGeocode});

  return countryCode ? String(countryCode).toUpperCase() : null;
}
